import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  EnvelopeSimple,
  Phone,
  MapPin,
  PaperPlaneTilt,
  CheckCircle,
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  'IT & Managed Services',
  'Software Development',
  'Biometric Equipment Installation',
  'Health App Solutions',
  'Satellite Services',
  'Advanced Workspace Management',
  'Project Management Consultancy',
  'IT Architecture Configuration',
  'Other',
];

export default function ContactPage() {
  const { theme } = useTheme();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service_interest: '',
    message: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    // Pre-fill service if coming from solutions page
    if (location.state?.service) {
      setFormData(prev => ({ ...prev, service_interest: location.state.service }));
    }
  }, [location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.service_interest || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div data-testid="contact-page" className={`min-h-screen pt-24 flex items-center justify-center ${
        theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
      }`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center px-4"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#00C8EB]/20 flex items-center justify-center">
            <CheckCircle size={48} weight="duotone" className="text-[#00C8EB]" />
          </div>
          <h1 className={`text-3xl md:text-4xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Thank You!
          </h1>
          <p className={`text-lg mb-8 max-w-md mx-auto ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            We've received your message and will get back to you within 24 hours.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                company: '',
                phone: '',
                service_interest: '',
                message: '',
              });
            }}
            data-testid="contact-new-message-btn"
            className="bg-[#464B8B] hover:bg-[#464B8B]/90"
          >
            Send Another Message
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className={`min-h-screen pt-24 ${
      theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <span className="text-[#00C8EB] font-medium uppercase tracking-wider text-sm">
              Contact Us
            </span>
            <h1 className={`text-4xl md:text-5xl font-bold mt-3 mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Get in <span className="text-gradient">Touch</span>
            </h1>
            <p className={`text-lg mb-10 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Ready to transform your technology infrastructure? Get in touch with our 
              team of experts and let's discuss your project.
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
                  <EnvelopeSimple size={24} weight="duotone" className="text-[#00C8EB]" />
                </div>
                <div>
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Email
                  </p>
                  <a href="mailto:contact@atlassoftech.com" className="text-[#00C8EB] hover:underline">
                    contact@atlassoftech.com
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
                  <Phone size={24} weight="duotone" className="text-[#00C8EB]" />
                </div>
                <div>
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Phone
                  </p>
                  <a href="tel:+255799227202" className="text-[#00C8EB] hover:underline">
                    +255 799 227 202
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#00C8EB]/10 flex items-center justify-center">
                  <MapPin size={24} weight="duotone" className="text-[#00C8EB]" />
                </div>
                <div>
                  <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Location
                  </p>
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                    Dar Es Salaam, Tanzania
                  </p>
                </div>
              </div>
            </div>

            {/* Trust indicators */}
            <div className={`mt-12 p-6 rounded-2xl ${
              theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-white shadow-sm'
            }`}>
              <p className={`font-medium mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Why Choose ATLAS?
              </p>
              <div className="space-y-3">
                {[
                  'Response within 24 hours',
                  'Free initial consultation',
                  'Enterprise-grade solutions',
                  'Dedicated support team',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle size={20} weight="duotone" className="text-[#00C8EB]" />
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' 
                ? 'bg-[#12131F] border border-[#464B8B]/20' 
                : 'bg-white shadow-lg'
            }`}>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Full Name *
                    </label>
                    <Input
                      placeholder="John Doe"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      data-testid="contact-name-input"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      placeholder="john@company.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      data-testid="contact-email-input"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Company
                    </label>
                    <Input
                      placeholder="Company Inc."
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      data-testid="contact-company-input"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      placeholder="+255 xxx xxx xxx"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      data-testid="contact-phone-input"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Service Interest *
                  </label>
                  <Select
                    value={formData.service_interest}
                    onValueChange={(value) => handleInputChange('service_interest', value)}
                  >
                    <SelectTrigger 
                      data-testid="contact-service-select"
                      className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30' : ''}
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceOptions.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Message *
                  </label>
                  <Textarea
                    placeholder="Tell us about your project requirements..."
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    data-testid="contact-message-input"
                    rows={5}
                    className={theme === 'dark' ? 'bg-[#0A0A10] border-[#464B8B]/30 resize-none' : 'resize-none'}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isLoading}
                  data-testid="contact-submit-btn"
                  className="w-full bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10] font-semibold h-12"
                >
                  {isLoading ? (
                    'Sending...'
                  ) : (
                    <>
                      <PaperPlaneTilt size={20} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>

                <p className={`text-center text-sm ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  By submitting, you agree to our Privacy Policy
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
